import { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Printer, Share2, X, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Phone, MessageCircle, RotateCcw, AlertTriangle, FileText } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const API = `${window.location.origin}/api`;

// أنواع الوصولات
const receiptTypes = {
  sale_cash: { ar: "بيع نقداً", en: "Cash Sale", color: "bg-green-600" },
  sale_credit: { ar: "بيع بالدين", en: "Credit Sale", color: "bg-amber-600" },
  purchase: { ar: "مشتريات", en: "Purchase", color: "bg-blue-600" },
  expense: { ar: "مصاريف تشغيلية", en: "Operating Expense", color: "bg-red-600" },
  customer_payment: { ar: "تسديد دين زبون", en: "Customer Payment", color: "bg-green-500" },
  supplier_payment: { ar: "دفع للمورد", en: "Supplier Payment", color: "bg-orange-500" },
  salary: { ar: "راتب موظف", en: "Employee Salary", color: "bg-purple-600" },
  damage: { ar: "تلفيات", en: "Damages", color: "bg-red-500" },
};

// مكون الوصل الموحد
const UniversalReceipt = ({ data, type, shop, language = "ar" }) => {
  const isArabic = language === "ar";
  const receiptType = receiptTypes[type] || receiptTypes.sale_cash;
  
  return (
    <div 
      className="bg-white text-black p-6 font-mono text-sm"
      style={{ direction: isArabic ? "rtl" : "ltr" }}
    >
      {/* Header - Shop Info */}
      <div className="text-center border-b-2 border-dashed border-gray-400 pb-4 mb-4">
        {/* Logo */}
        <div className="flex justify-center mb-2">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-8 h-8">
              <path d="M4 4a2 2 0 012-2h12a2 2 0 012 2v16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm14 0H6v16h12V4zM8 6h8v4H8V6zm0 6h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-8 4h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
            </svg>
          </div>
        </div>
        <h1 className="text-xl font-bold">{shop?.shop_name || "حساباتي"}</h1>
        <p className="text-xs text-gray-600">{isArabic ? "نظام الحسابات" : "Accounting System"}</p>
        
        {/* معلومات المتجر */}
        {shop?.address && <p className="text-xs mt-1">{shop.address}</p>}
        {shop?.phone && <p className="text-xs">{isArabic ? "هاتف:" : "Phone:"} {shop.phone}</p>}
        {shop?.whatsapp_number && <p className="text-xs">{isArabic ? "واتساب:" : "WhatsApp:"} {shop.whatsapp_number}</p>}
      </div>

      {/* نوع الوصل */}
      <div className={`text-center py-2 mb-4 rounded ${receiptType.color} text-white font-bold`}>
        {isArabic ? receiptType.ar : receiptType.en}
      </div>

      {/* معلومات الوصل */}
      <div className="border-b border-dashed border-gray-400 pb-3 mb-3 space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">{isArabic ? "رقم الوصل:" : "Receipt #:"}</span>
          <span className="font-bold text-xs">{data?.invoice_number || data?.id?.slice(0, 8).toUpperCase()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{isArabic ? "التاريخ:" : "Date:"}</span>
          <span>{new Date(data?.created_at || new Date()).toLocaleDateString(isArabic ? "ar-IQ" : "en-US")}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{isArabic ? "الوقت:" : "Time:"}</span>
          <span>{new Date(data?.created_at || new Date()).toLocaleTimeString(isArabic ? "ar-IQ" : "en-US", { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        
        {/* معلومات إضافية حسب النوع */}
        {(type === "sale_cash" || type === "sale_credit") && data?.customer_name && (
          <div className="flex justify-between">
            <span className="text-gray-600">{isArabic ? "الزبون:" : "Customer:"}</span>
            <span>{data.customer_name}</span>
          </div>
        )}
        {type === "purchase" && data?.supplier_name && (
          <div className="flex justify-between">
            <span className="text-gray-600">{isArabic ? "المورد:" : "Supplier:"}</span>
            <span>{data.supplier_name}</span>
          </div>
        )}
        {type === "expense" && data?.expense_type && (
          <div className="flex justify-between">
            <span className="text-gray-600">{isArabic ? "نوع المصروف:" : "Expense Type:"}</span>
            <span>{data.expense_type}</span>
          </div>
        )}
        {type === "salary" && data?.employee_name && (
          <div className="flex justify-between">
            <span className="text-gray-600">{isArabic ? "الموظف:" : "Employee:"}</span>
            <span>{data.employee_name}</span>
          </div>
        )}
      </div>

      {/* المنتجات/العناصر */}
      {data?.items && data.items.length > 0 && (
        <div className="border-b border-dashed border-gray-400 pb-3 mb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-start py-1">{isArabic ? "الصنف" : "Item"}</th>
                <th className="text-center">{isArabic ? "الكمية" : "Qty"}</th>
                <th className="text-center">{isArabic ? "السعر" : "Price"}</th>
                <th className="text-end">{isArabic ? "المجموع" : "Total"}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-1">{item.product_name}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-center">{item.price?.toLocaleString()}</td>
                  <td className="text-end">{item.total?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* التفاصيل المالية */}
      <div className="space-y-2 mb-4">
        {/* المجموع */}
        <div className="flex justify-between text-lg font-bold border-t-2 border-black pt-2">
          <span>{isArabic ? "المجموع:" : "Total:"}</span>
          <span>{(data?.total || data?.amount || 0).toLocaleString()} د.ع</span>
        </div>
        
        {/* تفاصيل الدفع للمبيعات/المشتريات بالدين */}
        {(type === "sale_credit" || (type === "purchase" && data?.payment_type === "credit")) && (
          <>
            <div className="flex justify-between text-green-600">
              <span>{isArabic ? "المدفوع:" : "Paid:"}</span>
              <span>{(data?.amount_paid || 0).toLocaleString()} د.ع</span>
            </div>
            <div className="flex justify-between text-red-600 font-bold">
              <span>{isArabic ? "المتبقي:" : "Remaining:"}</span>
              <span>{(data?.remaining || (data?.total - (data?.amount_paid || 0))).toLocaleString()} د.ع</span>
            </div>
            {data?.due_date && (
              <div className="flex justify-between text-amber-600">
                <span>{isArabic ? "تاريخ الاستحقاق:" : "Due Date:"}</span>
                <span>{new Date(data.due_date).toLocaleDateString(isArabic ? "ar-IQ" : "en-US")}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* الملاحظات */}
      {(data?.description || data?.notes) && (
        <div className="border-t border-dashed border-gray-400 pt-3 mb-3">
          <p className="text-xs text-gray-600">{isArabic ? "ملاحظات:" : "Notes:"}</p>
          <p className="text-sm">{data.description || data.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="text-center border-t-2 border-dashed border-gray-400 pt-4 mt-4">
        <p className="text-sm font-bold">
          {isArabic ? "شكراً لتعاملكم معنا" : "Thank you for your business"}
        </p>
        <p className="text-xs text-gray-500 mt-2">
          {isArabic ? "نتمنى لكم يوماً سعيداً" : "Have a great day!"}
        </p>
        
        {/* روابط التواصل */}
        {(shop?.facebook || shop?.instagram || shop?.tiktok) && (
          <div className="mt-3 text-xs text-gray-500">
            {shop?.facebook && <span className="mx-1">FB: {shop.facebook}</span>}
            {shop?.instagram && <span className="mx-1">IG: {shop.instagram}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export const ReceiptDialog = ({ 
  isOpen, 
  onClose, 
  data,
  type = "sale_cash", // sale_cash, sale_credit, purchase, expense, etc.
  language = "ar",
  // ميزة التنقل بين الفواتير (اختيارية)
  navigation = null, // { currentIndex, total, onFirst, onPrev, onNext, onLast }
  // ✨ تفعيل زر المرتجع — يظهر في صفحة المبيعات فقط (ليس بعد إتمام البيع مباشرة)
  allowReturn = false,
  onReturned = null,  // callback after successful return
}) => {
  const receiptRef = useRef(null);
  const navigate = useNavigate();
  const [shop, setShop] = useState({});
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnNotes, setReturnNotes] = useState("");
  const [returnToInventory, setReturnToInventory] = useState(true);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const isArabic = language === "ar";

  // جلب معلومات المتجر
  useEffect(() => {
    const fetchShop = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setShop(response.data);
      } catch (error) {
        console.error("Error fetching shop info:", error);
      }
    };
    if (isOpen) {
      fetchShop();
    }
  }, [isOpen]);

  // تحديد نوع الوصل تلقائياً
  const getReceiptType = () => {
    if (type === "purchase") return "purchase";
    if (type === "expense") return "expense";
    if (type === "salary") return "salary";
    if (type === "damage") return "damage";
    if (type === "customer_payment") return "customer_payment";
    if (type === "supplier_payment") return "supplier_payment";
    
    // للمبيعات
    if (data?.payment_type === "credit") return "sale_credit";
    return "sale_cash";
  };

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt-${data?.id?.slice(0, 8)}`,
  });

  const generateWhatsAppMessage = () => {
    if (!data) return "";
    
    const receiptType = receiptTypes[getReceiptType()];
    let message = isArabic 
      ? `*وصل ${receiptType.ar} من ${shop?.shop_name || "حساباتي"}*\n\n`
      : `*${receiptType.en} Receipt from ${shop?.shop_name || "Hesabaty"}*\n\n`;
    
    message += isArabic 
      ? `📅 التاريخ: ${new Date(data.created_at).toLocaleDateString("ar-IQ")}\n`
      : `📅 Date: ${new Date(data.created_at).toLocaleDateString("en-US")}\n`;
    
    message += isArabic 
      ? `🔢 رقم الوصل: ${data.id?.slice(0, 8).toUpperCase()}\n`
      : `🔢 Receipt #: ${data.id?.slice(0, 8).toUpperCase()}\n`;
    
    // المنتجات إذا وجدت
    if (data.items?.length > 0) {
      message += isArabic ? `\n*المنتجات:*\n` : `\n*Items:*\n`;
      data.items.forEach((item, index) => {
        message += `${index + 1}. ${item.product_name} × ${item.quantity} = ${item.total.toLocaleString()} د.ع\n`;
      });
    }
    
    message += `\n━━━━━━━━━━━━━━━\n`;
    message += isArabic 
      ? `💰 *المجموع: ${(data.total || data.amount || 0).toLocaleString()} د.ع*\n`
      : `💰 *Total: ${(data.total || data.amount || 0).toLocaleString()} د.ع*\n`;
    
    if (data.payment_type === "credit" && data.remaining > 0) {
      message += isArabic 
        ? `✅ المدفوع: ${(data.amount_paid || 0).toLocaleString()} د.ع\n`
        : `✅ Paid: ${(data.amount_paid || 0).toLocaleString()} د.ع\n`;
      message += isArabic 
        ? `⏳ المتبقي: ${data.remaining.toLocaleString()} د.ع\n`
        : `⏳ Remaining: ${data.remaining.toLocaleString()} د.ع\n`;
    }
    
    message += `\n${isArabic ? "شكراً لتعاملكم معنا 🙏" : "Thank you for your business 🙏"}`;
    
    if (shop?.phone) {
      message += `\n${isArabic ? "📞 للتواصل:" : "📞 Contact:"} ${shop.phone}`;
    }
    
    return encodeURIComponent(message);
  };

  const handleShareWhatsApp = (phoneNumber = "") => {
    const message = generateWhatsAppMessage();
    let cleanPhone = phoneNumber?.replace(/\s+/g, "").replace(/^0/, "964") || "";
    if (cleanPhone && !cleanPhone.startsWith("+") && !cleanPhone.startsWith("964")) {
      cleanPhone = "964" + cleanPhone;
    }
    
    const whatsappUrl = cleanPhone 
      ? `https://wa.me/${cleanPhone}?text=${message}`
      : `https://wa.me/?text=${message}`;
    
    window.open(whatsappUrl, "_blank");
  };

  // ✨ فتح محادثة WhatsApp بدون رسالة معبأة (شات مباشر)
  const handleWhatsAppChat = (phoneNumber = "") => {
    let cleanPhone = phoneNumber?.replace(/\s+/g, "").replace(/^0/, "964") || "";
    if (cleanPhone && !cleanPhone.startsWith("+") && !cleanPhone.startsWith("964")) {
      cleanPhone = "964" + cleanPhone;
    }
    if (!cleanPhone) {
      toast.error(isArabic ? "لا يوجد رقم هاتف للزبون" : "No customer phone");
      return;
    }
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  // ✨ اتصال هاتفي مباشر
  const handlePhoneCall = (phoneNumber = "") => {
    if (!phoneNumber) {
      toast.error(isArabic ? "لا يوجد رقم هاتف للزبون" : "No customer phone");
      return;
    }
    const cleanPhone = phoneNumber.replace(/\s+/g, "");
    window.location.href = `tel:${cleanPhone}`;
  };

  // ✨ إنشاء فاتورة مرتجع (بدلاً من حذف الفاتورة الأصلية)
  const handleSubmitReturn = async () => {
    if (!returnReason.trim()) {
      toast.error(isArabic ? "أدخل سبب الإرجاع" : "Enter return reason");
      return;
    }
    if (!data?.items || data.items.length === 0) {
      toast.error(isArabic ? "لا توجد عناصر للإرجاع" : "No items to return");
      return;
    }
    setSubmittingReturn(true);
    try {
      const items = data.items.map((it) => ({
        product_id: it.product_id || "",
        product_name: it.product_name || it.name || "",
        quantity: it.quantity || 1,
        unit_price: it.price || 0,
        reason: returnReason.trim(),
      }));
      const payload = {
        sale_id: data.id || null,
        customer_id: data.customer_id || null,
        customer_name: data.customer_name || null,
        items,
        reason: returnReason.trim(),
        notes: returnNotes.trim() || null,
        return_to_inventory: returnToInventory,
      };
      await axios.post(`${API}/returns`, payload);
      toast.success(isArabic ? "تم إنشاء فاتورة المرتجع ✓" : "Return invoice created ✓");
      setShowReturnModal(false);
      setReturnReason("");
      setReturnNotes("");
      if (onReturned) onReturned();
      onClose && onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "";
      toast.error(isArabic ? `فشل إنشاء المرتجع: ${msg}` : `Return failed: ${msg}`);
    } finally {
      setSubmittingReturn(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center justify-between">
            <span>{isArabic ? "الوصل" : "Receipt"}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        {/* Navigation Bar — أسهم التنقل بين الفواتير */}
        {navigation && navigation.total > 0 && (
          <div className="flex items-center justify-between gap-2 px-2 py-2 bg-slate-900/60 border border-slate-700 rounded-lg">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={navigation.onFirst}
                disabled={navigation.currentIndex <= 0}
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30"
                title={isArabic ? "أول فاتورة" : "First"}
                data-testid="nav-first-invoice-btn"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={navigation.onPrev}
                disabled={navigation.currentIndex <= 0}
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30"
                title={isArabic ? "السابقة" : "Previous"}
                data-testid="nav-prev-invoice-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm" data-testid="invoice-navigation-counter">
              <span className="text-slate-400">{isArabic ? "فاتورة" : "Invoice"}</span>
              <span className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold px-3 py-1 rounded-full" dir="ltr">
                {navigation.currentIndex + 1} / {navigation.total}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={navigation.onNext}
                disabled={navigation.currentIndex >= navigation.total - 1}
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30"
                title={isArabic ? "التالية" : "Next"}
                data-testid="nav-next-invoice-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={navigation.onLast}
                disabled={navigation.currentIndex >= navigation.total - 1}
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30"
                title={isArabic ? "آخر فاتورة" : "Last"}
                data-testid="nav-last-invoice-btn"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Receipt Preview */}
        <div className="bg-white rounded-lg overflow-hidden" ref={receiptRef}>
          <UniversalReceipt 
            data={data} 
            type={getReceiptType()} 
            shop={shop} 
            language={language} 
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={handlePrint}
            className="flex-1 bg-red-600 hover:bg-red-700"
            data-testid="receipt-print-btn"
          >
            <Printer className="w-4 h-4 me-2" />
            {isArabic ? "طباعة" : "Print"}
          </Button>
          <Button
            onClick={() => handleShareWhatsApp(data?.customer_phone || shop?.whatsapp_number)}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="receipt-whatsapp-share-btn"
          >
            <Share2 className="w-4 h-4 me-2" />
            {isArabic ? "إرسال الفاتورة" : "Send receipt"}
          </Button>
        </div>

        {/* ✨ Customer contact actions — اتصال + شات WhatsApp */}
        {data?.customer_phone && (
          <div className="flex gap-2">
            <Button
              onClick={() => handlePhoneCall(data.customer_phone)}
              variant="outline"
              size="sm"
              className="flex-1 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              data-testid="receipt-call-customer-btn"
            >
              <Phone className="w-4 h-4 me-2" />
              {isArabic ? "اتصال" : "Call"}
            </Button>
            <Button
              onClick={() => handleWhatsAppChat(data.customer_phone)}
              variant="outline"
              size="sm"
              className="flex-1 border-green-500/50 text-green-400 hover:bg-green-500/10"
              data-testid="receipt-whatsapp-chat-btn"
            >
              <MessageCircle className="w-4 h-4 me-2" />
              {isArabic ? "محادثة WhatsApp" : "WhatsApp chat"}
            </Button>
          </div>
        )}

        {/* ✨ Return Invoice button — يظهر فقط في صفحة المبيعات */}
        {allowReturn && (type === "sale_cash" || type === "sale_credit") && data?.id && (
          <Button
            onClick={() => setShowReturnModal(true)}
            variant="outline"
            className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            data-testid="receipt-return-invoice-btn"
          >
            <RotateCcw className="w-4 h-4 me-2" />
            {isArabic ? "إنشاء فاتورة مرتجع" : "Create return invoice"}
          </Button>
        )}

        {/* ✨ Account Statement button — يظهر دائماً للفواتير المرتبطة بزبون */}
        {(type === "sale_cash" || type === "sale_credit") && data?.customer_id && (
          <Button
            onClick={() => {
              onClose && onClose();
              navigate(`/account-statement?type=customer&customer_id=${encodeURIComponent(data.customer_id)}`);
            }}
            variant="outline"
            className="w-full border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="receipt-account-statement-btn"
          >
            <FileText className="w-4 h-4 me-2" />
            {isArabic ? "كشف حساب الزبون" : "Customer account statement"}
          </Button>
        )}

        {/* Quick Share Options */}
        <div className="flex gap-2 text-sm">
          {data?.customer_phone && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleShareWhatsApp(data.customer_phone)}
              className="flex-1 border-green-600 text-green-500 hover:bg-green-600 hover:text-white"
            >
              {isArabic ? "إرسال للزبون" : "Send to Customer"}
            </Button>
          )}
          {shop?.whatsapp_number && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleShareWhatsApp(shop.whatsapp_number)}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              {isArabic ? "إرسال للإدارة" : "Send to Admin"}
            </Button>
          )}
        </div>

        {/* ✨ Return Invoice Modal — نموذج إنشاء فاتورة مرتجع */}
        {showReturnModal && (
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => !submittingReturn && setShowReturnModal(false)}
            data-testid="return-invoice-modal"
          >
            <div
              className="bg-slate-900 border border-orange-500/30 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-orange-500/10">
                <h3 className="text-orange-300 font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  {isArabic ? "إنشاء فاتورة مرتجع" : "Create return invoice"}
                </h3>
                <button
                  onClick={() => !submittingReturn && setShowReturnModal(false)}
                  className="text-slate-400 hover:text-white"
                  data-testid="return-modal-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 space-y-3">
                <p className="text-xs text-slate-400">
                  {isArabic
                    ? "للحفاظ على سلامة المحاسبة، لا يمكن حذف الفاتورة. سيتم إنشاء فاتورة مرتجع مرتبطة بالفاتورة الأصلية."
                    : "To preserve accounting integrity, the invoice cannot be deleted. A return invoice will be created and linked to the original."}
                </p>

                <div>
                  <label className="text-slate-200 text-sm block mb-1">
                    {isArabic ? "سبب الإرجاع *" : "Return reason *"}
                  </label>
                  <input
                    type="text"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder={isArabic ? "مثال: منتج تالف، تغيير رأي..." : "e.g. damaged item, change of mind..."}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="return-reason-input"
                  />
                </div>

                <div>
                  <label className="text-slate-200 text-sm block mb-1">
                    {isArabic ? "ملاحظات (اختياري)" : "Notes (optional)"}
                  </label>
                  <textarea
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="return-notes-input"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={returnToInventory}
                    onChange={(e) => setReturnToInventory(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="return-to-inventory-checkbox"
                  />
                  {isArabic ? "إعادة الكميات إلى المخزون" : "Return quantities to inventory"}
                </label>

                <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">
                    {isArabic ? "العناصر التي سيتم إرجاعها:" : "Items to return:"}
                  </p>
                  <ul className="text-xs text-slate-300 space-y-1 max-h-24 overflow-y-auto">
                    {data?.items?.map((it, idx) => (
                      <li key={idx} className="flex justify-between">
                        <span>{it.product_name || it.name} × {it.quantity}</span>
                        <span className="font-mono">{(it.total || (it.price * it.quantity) || 0).toLocaleString()} د.ع</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex gap-2 p-3 border-t border-slate-700 bg-slate-900/40">
                <Button
                  variant="outline"
                  onClick={() => setShowReturnModal(false)}
                  disabled={submittingReturn}
                  className="flex-1 border-slate-600 text-slate-300"
                  data-testid="return-cancel-btn"
                >
                  {isArabic ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                  onClick={handleSubmitReturn}
                  disabled={submittingReturn}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="return-confirm-btn"
                >
                  {submittingReturn
                    ? (isArabic ? "جاري الحفظ..." : "Saving...")
                    : (isArabic ? "تأكيد الإرجاع" : "Confirm return")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
